/**
 * FormPanel - Pannello laterale per form contabili
 *
 * Wrapper che apre i form in un pannello laterale invece di un modal centrato.
 * Segue lo stesso pattern di DetailPanel in apps/web.
 *
 * Desktop: pannello laterale destro
 * Mobile: modal full screen con X per chiudere
 */

import React, { useEffect, useState } from 'react';

// =============================================================================
// TYPES
// =============================================================================

export interface FormPanelProps {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Called when panel should close */
  onClose: () => void;
  /** Panel title */
  title: string;
  /** Subtitle */
  subtitle?: string;
  /** Icon component */
  icon?: React.ReactNode;
  /** Header color accent */
  headerColor?: string;
  /** Panel content */
  children: React.ReactNode;
  /** Footer content (actions) */
  footer?: React.ReactNode;
  /** Panel width (desktop only) */
  width?: number | string;
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  overlay: (isOpen: boolean) => ({
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(0, 0, 0, 0.4)',
    zIndex: 1000,
    opacity: isOpen ? 1 : 0,
    visibility: isOpen ? 'visible' : 'hidden',
    transition: 'opacity 0.2s ease, visibility 0.2s ease',
  }),
  panel: (isOpen: boolean, width: number | string) => ({
    position: 'fixed' as const,
    top: 0,
    right: 0,
    bottom: 0,
    width: typeof width === 'number' ? `${width}px` : width,
    maxWidth: '100vw',
    background: 'var(--bg, #fff)',
    boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    flexDirection: 'column' as const,
    transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
    transition: 'transform 0.25s ease',
    zIndex: 1001,
  }),
  header: (headerColor?: string) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid var(--line, #e5e7eb)',
    background: headerColor ? `linear-gradient(135deg, ${headerColor}15, transparent)` : undefined,
    flexShrink: 0,
  }),
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  iconBox: (headerColor?: string) => ({
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    background: headerColor || 'var(--brand, #3b82f6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    flexShrink: 0,
  }),
  title: {
    fontSize: '16px',
    fontWeight: 600,
    margin: 0,
    color: 'var(--text, #111827)',
  },
  subtitle: {
    fontSize: '13px',
    color: 'var(--text-muted, #6b7280)',
    marginTop: '2px',
  },
  closeBtn: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-muted, #6b7280)',
    transition: 'background 0.15s',
  },
  body: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '20px',
  },
  footer: {
    padding: '16px 20px',
    borderTop: '1px solid var(--line, #e5e7eb)',
    background: 'var(--bg-secondary, #f9fafb)',
    flexShrink: 0,
  },
};

// =============================================================================
// COMPONENT
// =============================================================================

export function FormPanel({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  headerColor,
  children,
  footer,
  width = 600,
}: FormPanelProps) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const panelWidth = isMobile ? '100vw' : width;

  return (
    <>
      {/* Overlay */}
      <div style={styles.overlay(isOpen)} onClick={onClose} />

      {/* Panel */}
      <div
        style={styles.panel(isOpen, panelWidth)}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={styles.header(headerColor)}>
          <div style={styles.headerLeft}>
            {icon && <div style={styles.iconBox(headerColor)}>{icon}</div>}
            <div>
              <h2 style={styles.title}>{title}</h2>
              {subtitle && <p style={styles.subtitle}>{subtitle}</p>}
            </div>
          </div>
          <button
            style={styles.closeBtn}
            onClick={onClose}
            aria-label="Chiudi"
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--bg-secondary, #f3f4f6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>{children}</div>

        {/* Footer */}
        {footer && <div style={styles.footer}>{footer}</div>}
      </div>
    </>
  );
}

// =============================================================================
// FORM PANEL ACTIONS
// =============================================================================

interface FormPanelActionsProps {
  children: React.ReactNode;
}

export function FormPanelActions({ children }: FormPanelActionsProps) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
      {children}
    </div>
  );
}
